// Block model for the report editor (SPEC/1466 phase 3, react-pdf variant).
//
// The report is an ordered list of blocks the user adds / reorders / removes,
// rather than a fixed title→intro→charts→conclusions shape. Each block renders
// to a react-pdf primitive (see ReportDocument). The chart's own title/subtitle
// stay baked into the captured PNG, so a chart block carries NO separate text
// heading — if the user wants a section title they add a `heading` block.
//
// ORDER lives in this array's positions; IDENTITY lives in the reference. Keeping
// them apart is what makes reordering safe, and it is why a `chart` block names
// the CONFIGURED CHART (`instanceId`, see lib/algorithmInstance) rather than the
// figure a particular run drew.
//
// A chart id is an artifact of one run. It used to be `histogram-histogram`, then
// `-2` for a second occurrence of the algorithm, and it MOVED: delete the first of
// two histograms, re-execute, and the survivor inherited the unsuffixed id — so a
// block pointing at it silently rendered a DIFFERENT chart under the caption its
// author wrote. In a document that goes to a client, quietly showing the wrong
// figure is the worst failure available.
//
// Chart ids no longer move: one derives from the configured chart's identity plus,
// for a category split, the category itself. The reference here stays on the
// INSTANCE anyway, because a chart id is per-run and cannot name a selection meant
// to outlive the run. Referencing the instance either resolves to the chart the user
// picked, or to nothing — and the editor says so.
//
// Which of that instance's figures is named by whatever cannot move:
//   • a category split → its `category`;
//   • `error`          → its BASE (lp=0, cp=1, sp=2), since which bases it draws is
//                        presentation-only and changes with no re-run;
//   • everything else  → `figureIndex`, its position, which is safe because those
//                        instances own exactly one figure.
// So an instance's indices are not necessarily contiguous. See reportFigures.

import { errorBaseSlot } from '../config/chartIntent';
import { randomSuffix } from './algorithmInstance';
import type { ChartNote } from '../types/api';

// A `note` block carries no text: it POINTS at a note written on the dashboard
// (chart_notes, keyed by chart id), and `noteTexts` resolves it at render time.
// A binding, not a copy — the copies the first version seeded were taken once,
// when the report was first opened, so a note written or edited afterwards never
// reached the document.
//
// A `title` block is the document's own heading — there is exactly one concept
// by that name now. It used to also be a document-level field (`docTitle`,
// rendered outside the block list), which collided in name with the `heading`
// block (labelled "Título" in the UI) without being the same thing — editing
// one looked like it should affect the other. `heading` remains the in-body
// section marker, distinct from it.
export type ReportBlock =
  | { id: string; kind: 'title'; text: string }
  | { id: string; kind: 'heading'; text: string }
  | { id: string; kind: 'paragraph'; text: string }
  | {
      id: string;
      kind: 'chart';
      instanceId: string;
      figureIndex: number;
      /** Set when the figure is one of a category split: then it, not the index,
       *  is what the block follows. See reportFigures. */
      category?: string;
      caption: string;
    }
  | { id: string; kind: 'image'; dataUrl: string }
  | {
      id: string;
      kind: 'note';
      instanceId: string;
      figureIndex: number;
      category?: string;
      /** Which of the figure's two notes this is. */
      position: 'before' | 'after';
    }
  | { id: string; kind: 'pagebreak' };

/** One selectable figure: which configured chart it came from, and which of its
 *  figures it is. `chartId` is the run artifact used to look up the rendering. */
export interface ReportFigure {
  instanceId: string;
  figureIndex: number;
  /** The category this figure was filtered to, when it is one of a split. */
  category?: string;
  chartId: string;
  title: string;
}

/**
 * The figures of the current dashboard, in grid order, each tagged with the
 * configured chart that produced it. What the block selector offers and what a
 * block's reference resolves against.
 */
export function reportFigures(
  charts: { id: string; title?: string }[],
  instanceMap: Record<string, string>,
  categoryMap: Record<string, string> = {}
): ReportFigure[] {
  const perInstance = new Map<string, number>();
  const out: ReportFigure[] = [];
  for (const chart of charts) {
    const instanceId = instanceMap[chart.id];
    // A chart nothing attributes to an instance cannot be referenced stably, so
    // it is not offered — rather than offered and silently repointed later.
    if (!instanceId) continue;
    const positional = perInstance.get(instanceId) ?? 0;
    perInstance.set(instanceId, positional + 1);
    // An `error` chart is numbered by its BASE (lp=0, cp=1, sp=2), not by its
    // position among the bases the user happens to have ticked: unticking CP
    // redraws [RSE_LP, MSRE] with no re-run, and a block pointing at "the second
    // figure" would land on MSRE under a caption about RSE_CP. By base it resolves
    // to nothing instead.
    //
    // A by-category fan-out has the same problem and is answered the same way: the
    // figure carries its CATEGORY, and a block that has one follows that instead of
    // the index (resolveFigure). Unpicking a category moves every later figure's
    // position, so an index would have sent a caption written for one category over
    // another's curve — in the client's PDF. The index is still emitted, because it
    // is what everything without a category is referenced by.
    const category = categoryMap[chart.id];
    const figureIndex = errorBaseSlot(chart.id) ?? positional;
    out.push({
      instanceId,
      figureIndex,
      ...(category !== undefined ? { category } : {}),
      chartId: chart.id,
      title: chart.title || chart.id,
    });
  }
  return out;
}

/**
 * The figure a chart block points at, or undefined when it no longer exists.
 *
 * A block with a `category` is matched on THAT and not on the index: within one
 * configured chart the category names the figure, while the index only says where it
 * happened to sit. Matching both would lose a figure that merely shifted when a
 * sibling was unpicked; matching the index alone would silently hand the block its
 * neighbour.
 */
export function resolveFigure(
  block: { instanceId: string; figureIndex: number; category?: string },
  figures: ReportFigure[]
): ReportFigure | undefined {
  if (block.category !== undefined) {
    return figures.find((f) => f.instanceId === block.instanceId && f.category === block.category);
  }
  return figures.find(
    (f) => f.instanceId === block.instanceId && f.figureIndex === block.figureIndex
  );
}

// An `<option value>` is a single string, so the reference is encoded for the picker
// and decoded straight back into the block's fields. This is transport, not meaning:
// nothing reads the instance id out of it to learn the algorithm or a position.
//
// Still parsed from the RIGHT, because an instance id may itself contain the
// separator (pinned by a test below). A CATEGORY may contain anything at all — it is
// the client's data — so it is percent-encoded, which removes the separator from it,
// and prefixed with a marker: that is what tells a three-part key from a two-part
// one without having to guess whether a trailing segment is a number or a category
// that happens to look like one.
const FIGURE_KEY_SEPARATOR = '@';
const CATEGORY_MARKER = 'cat:';
// `category` is always PRESENT, undefined when there is none. A patch built from
// this is spread over the block it edits, and an omitted key leaves the previous
// value behind: re-pointing a block from a split figure to a plain one kept the old
// category, so it resolved to nothing and the row declared itself broken while the
// picker showed a perfectly valid choice.
const NO_FIGURE: FigureRef = Object.freeze({ instanceId: '', figureIndex: 0, category: undefined });

/** How a block refers to one figure. */
export interface FigureRef {
  instanceId: string;
  figureIndex: number;
  category: string | undefined;
}

export function figureKey({
  instanceId,
  figureIndex,
  category,
}: {
  instanceId: string;
  figureIndex: number;
  category?: string;
}): string {
  // With a category the index is PINNED to 0 and not the figure's position, because
  // `resolveFigure` ignores it there and this string is a controlled `<select>`
  // value: carrying the position made the key of a block written for the third
  // figure stop matching any option the moment a sibling was unpicked, and the
  // dropdown rendered blank over a preview that was perfectly correct.
  if (category !== undefined) {
    return `${instanceId}${FIGURE_KEY_SEPARATOR}0${FIGURE_KEY_SEPARATOR}${CATEGORY_MARKER}${encodeURIComponent(category)}`;
  }
  return `${instanceId}${FIGURE_KEY_SEPARATOR}${figureIndex}`;
}

export function parseFigureKey(key: string): FigureRef {
  const last = key.lastIndexOf(FIGURE_KEY_SEPARATOR);
  if (last <= 0) return NO_FIGURE;
  const tail = key.slice(last + 1);
  if (!tail.startsWith(CATEGORY_MARKER)) return parseHead(key);
  const head = parseHead(key.slice(0, last));
  if (!head.instanceId) return NO_FIGURE;
  return { ...head, category: decodeURIComponent(tail.slice(CATEGORY_MARKER.length)) };
}

/** The `instanceId@figureIndex` half, parsed from the right. */
function parseHead(key: string): FigureRef {
  const at = key.lastIndexOf(FIGURE_KEY_SEPARATOR);
  if (at <= 0) return NO_FIGURE;
  const figureIndex = Number(key.slice(at + 1));
  if (!Number.isInteger(figureIndex) || figureIndex < 0) return NO_FIGURE;
  return { instanceId: key.slice(0, at), figureIndex, category: undefined };
}

export type ReportBlockKind = ReportBlock['kind'];

// A patch for a block of kind K — only that kind's editable fields (never id or
// kind). Keying the patch to K makes a cross-kind edit (e.g. a chartId on a
// paragraph) a compile error, so the store needs no unsafe cast to merge it.
export type BlockPatch<K extends ReportBlockKind = ReportBlockKind> = Partial<
  Omit<Extract<ReportBlock, { kind: K }>, 'id' | 'kind'>
>;

// Block identities are React keys, store lookups AND the key `chartImages` is
// indexed by (see ReportDocument) — and since D2 they are PERSISTED, coming back
// from the server on the next visit. A monotonic counter was enough while a report
// lived only in the session; it stopped being enough the moment the ids outlived
// it, because a counter restarting at 1 hands a new block the identity of one
// already in the document, and two blocks sharing a `chartImages` entry means one
// figure under two captions — or the wrong figure under one — in a client's PDF.
// Random, so uniqueness holds without any caller assembling the set of ids in use.
const nextId = (): string => `blk-${randomSuffix()}`;

// (Main's counter is deliberately NOT kept: blockOrderKey needs ids to be
// UNIQUE, not monotonic, and random ids satisfy that while surviving the
// round trip a counter cannot.)

/** A fresh, empty block of the given kind, ready for the user to fill in. */
export function newBlock(kind: ReportBlockKind): ReportBlock {
  switch (kind) {
    case 'title':
      return { id: nextId(), kind, text: '' };
    case 'heading':
      return { id: nextId(), kind, text: '' };
    case 'paragraph':
      return { id: nextId(), kind, text: '' };
    case 'chart':
      // Unreferenced until the user picks a figure; the row shows the picker.
      return { id: nextId(), kind, instanceId: '', figureIndex: 0, caption: '' };
    case 'image':
      return { id: nextId(), kind, dataUrl: '' };
    case 'note':
      // Unreferenced until the user aims it, like a fresh chart block.
      return { id: nextId(), kind, instanceId: '', figureIndex: 0, position: 'before' };
    case 'pagebreak':
      return { id: nextId(), kind };
  }
}

/** The reference half of a note block: how it names the figure it brackets. */
function figureRefOf(f: {
  instanceId: string;
  figureIndex: number;
  category?: string;
}): { instanceId: string; figureIndex: number; category?: string } {
  return {
    instanceId: f.instanceId,
    figureIndex: f.figureIndex,
    ...(f.category !== undefined ? { category: f.category } : {}),
  };
}

function noteBlock(
  f: { instanceId: string; figureIndex: number; category?: string },
  position: 'before' | 'after'
): ReportBlock {
  return { id: nextId(), kind: 'note', ...figureRefOf(f), position };
}

/**
 * The initial block list when first entering the report editor: the document
 * TITLE, an empty intro paragraph, then each figure BRACKETED BY ITS TWO NOTE
 * BLOCKS (D9) — note before, chart, note after — and an empty closing paragraph.
 *
 * The note blocks are seeded WHETHER OR NOT a note is written yet: the block binds
 * to the dashboard note rather than copying it, so the empty one is the slot a
 * note written later lands in.
 *
 * The chart blocks still get no heading and an empty caption — the chart's title
 * and subtitle live inside its captured PNG (D18), so a caption repeating them
 * would duplicate what the figure already says. The lámina code that D16 puts
 * there belongs to that change, not this one.
 */
export function defaultBlocks(figures: ReportFigure[]): ReportBlock[] {
  const paragraph = (text: string): ReportBlock => ({ id: nextId(), kind: 'paragraph', text });
  return [
    // The title block comes first (PR 1060): the report opens on its own title,
    // and reportTitleOf reads it back from here.
    { id: nextId(), kind: 'title', text: '' },
    paragraph(''),
    ...figures.flatMap((f): ReportBlock[] => [
      noteBlock(f, 'before'),
      {
        id: nextId(),
        kind: 'chart' as const,
        ...figureRefOf(f),
        caption: '',
      },
      noteBlock(f, 'after'),
    ]),
    paragraph(''),
  ];
}

/**
 * The text each note block renders, keyed by BLOCK id — same shape as the
 * `chartImages` map a chart block is rendered from.
 *
 * Resolved from `chart_notes` at render time, so a note edited on the dashboard
 * reaches the document untouched and a cleared one disappears from it. A block
 * whose figure no longer exists resolves to NOTHING rather than to a neighbour's
 * note, which would print one figure's commentary beside another's curve.
 */
export function noteTexts(
  blocks: ReportBlock[],
  figures: ReportFigure[],
  notes: Record<string, ChartNote>
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const block of blocks) {
    if (block.kind !== 'note') continue;
    const text = noteTextOf(block, figures, notes);
    if (text) out[block.id] = text;
  }
  return out;
}

/** The note on `block`'s side of its figure, or '' — figure gone, or no note. */
function noteTextOf(
  block: { instanceId: string; figureIndex: number; category?: string; position: 'before' | 'after' },
  figures: ReportFigure[],
  notes: Record<string, ChartNote>
): string {
  const figure = resolveFigure(block, figures);
  if (!figure) return '';
  const text = notes[figure.chartId]?.[block.position] ?? '';
  // Same rule as setChartNote: an emptied note is no note.
  return text.trim() ? text : '';
}

/**
 * A document composed before note blocks existed, brought onto them — otherwise
 * the fix reaches only reports nobody had composed yet. Such a document carries
 * either nothing, or the inert PARAGRAPH COPIES the first version of D9 seeded.
 *
 * A paragraph that still reads exactly like the note beside it IS that copy, so it
 * BECOMES the binding; inserting a block next to it would print the sentence
 * twice. A paragraph the user wrote does not match and is left alone.
 *
 * Keyed on "the document has no note block at all", so it runs once and a report
 * saved after it grows no further. Returns the SAME array when there is nothing
 * to do.
 */
export function migrateNoteBlocks(
  blocks: ReportBlock[],
  figures: ReportFigure[],
  notes: Record<string, ChartNote>
): ReportBlock[] {
  if (blocks.some((b) => b.kind === 'note')) return blocks;
  if (!blocks.some((b) => b.kind === 'chart')) return blocks;
  // Nothing to resolve against yet (the run isn't loaded), so a paragraph holding
  // the D9 copy cannot be told from one the user wrote. Migrate on a later entry.
  if (figures.length === 0) return blocks;

  const isCopyOf = (text: string, note: string) => !!note.trim() && text.trim() === note.trim();
  const out: ReportBlock[] = [];
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    if (block.kind !== 'chart') {
      out.push(block);
      continue;
    }
    const before = noteTextOf({ ...block, position: 'before' }, figures, notes);
    const after = noteTextOf({ ...block, position: 'after' }, figures, notes);

    const previous = out[out.length - 1];
    if (previous?.kind === 'paragraph' && isCopyOf(previous.text, before)) out.pop();
    out.push(noteBlock(block, 'before'), block, noteBlock(block, 'after'));

    const next = blocks[i + 1];
    if (next?.kind === 'paragraph' && isCopyOf(next.text, after)) i++;
  }
  return out;
}

// @react-pdf/renderer's <PDFViewer> can leave the live preview stale after a
// block's rendered content changes shape — reordered, added, removed, or
// re-pointed at a different figure/image — because its internal incremental
// re-render doesn't always repaint that (github.com/diegomura/react-pdf issues
// #3153, #771), while a fresh `pdf(...).toBlob()` always renders correctly from
// scratch, which is why the download is unaffected while the on-screen preview
// can lag behind it. Keying <PDFViewer> to this forces a full remount exactly
// when what's rendered actually changes, sidestepping that stale re-render.
//
// It stays stable across an edit to a block's own TEXT fields (paragraph/heading
// text, chart caption) so typing doesn't remount the viewer on every keystroke —
// those are believed to repaint correctly today; only the block's visual
// identity (id + position + which figure/image it points at) is covered here.
// Relies on ids staying unique per session (see `nextId`) — a future
// duplicate/paste of a block would need to mint a new id, or it silently
// defeats this key.
export function blockOrderKey(blocks: ReportBlock[]): string {
  return blocks
    .map((b) => {
      switch (b.kind) {
        case 'chart':
          return `${b.id}:chart:${b.instanceId}:${b.figureIndex}:${b.category ?? ''}`;
        case 'image':
          return `${b.id}:image:${b.dataUrl}`;
        default:
          return `${b.id}:${b.kind}`;
      }
    })
    .join('|');
}

/**
 * The document's title, read off its first `title` block (there is no
 * document-level title field any more — see the `ReportBlock` doc comment).
 * Empty when there is none, or its text hasn't been filled in.
 */
export function reportTitle(blocks: ReportBlock[]): string {
  return blocks.find((b): b is Extract<ReportBlock, { kind: 'title' }> => b.kind === 'title')?.text.trim() ?? '';
}

/** Insert `block` at index `at`, clamped to `[0, blocks.length]` (a fractional
 *  index truncates). Omitted or non-finite means append. Returns a new array. */
export function insertBlock(blocks: ReportBlock[], block: ReportBlock, at?: number): ReportBlock[] {
  const i =
    at === undefined || !Number.isFinite(at)
      ? blocks.length
      : Math.min(Math.max(Math.trunc(at), 0), blocks.length);
  return [...blocks.slice(0, i), block, ...blocks.slice(i)];
}

/** Move the block at `id` one slot up or down; no-op at the ends. Returns a new array. */
export function reorderBlocks(blocks: ReportBlock[], id: string, dir: 'up' | 'down'): ReportBlock[] {
  const i = blocks.findIndex((b) => b.id === id);
  if (i === -1) return blocks;
  const j = dir === 'up' ? i - 1 : i + 1;
  if (j < 0 || j >= blocks.length) return blocks;
  const next = blocks.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}
